import React from "react";
import { saveVisitorSession, loadVisitorSession, clearVisitorSession, visitorSummary, visitorStorage, mergeVisitorContext } from "../lib/vibebeeVisitorSession.mjs";
import VibeBeeResearchResult from "./VibeBeeResearchResult";
import { Bot, ExternalLink, ImagePlus, MapPin, Mic, MicOff, Send, ShieldCheck, Sparkles, Volume2, VolumeX, X } from "lucide-react";
import { askAdminVibeBeeLiveAgent, askPublicVibeBee, cancelVoiceInput, recordVibeBeeVoiceEvent, requestVibeBeeSpeech, startVoiceInput, supportsVoiceInput, VOICE_UI_STATES } from "../lib/vibebeeLiveAgent";
import { createQuartzPlaybackController, stopVibeBeeSpeech } from "../lib/vibebeeVoice";
import { createValidatedTextReveal, recordVibeBeeTiming } from "../lib/vibebeeProgressiveText";
import { publicContextEnvelope, publicConversationContext } from "../lib/vibebeeConversation.mjs";
import { rememberVibeBeeJourney, trackVibeBeeJourneyClick } from "../lib/vibebeeJourneyTracking";
import { getEventIntakeBatch, submitEventIntakeBatch } from "../lib/api";
import { addPosterFiles, clipboardImageFiles, explicitFacebookSourceUrl, MAX_VIBEBEE_POSTERS, pollEventIntakeBatch, revokePosterItems } from "../lib/vibebeePosterAttachments";

const QUICK_PROMPTS = [
  "🍽️ Where can I eat tonight?",
  "🎵 What’s on tonight?",
  "📍 What’s nearby?",
  "✨ Plan my night",
  "🍹 Food, drinks & music",
];

const SPEECH_PREFERENCE_KEY = "v2n_vibebee_spoken_answers";

function Card({ card, visitor = false }) {
  const href = card.external_url || card.path;
  const primaryJourneyEvent = card.type === "directions" || card.action_label === "Take Me There"
    ? "vibebee_take_me_there"
    : "vibebee_venue_view";
  return (
    <div className="min-w-0 rounded-2xl border border-white/10 bg-white/[0.04] p-3">
      <p className="break-words text-sm font-black text-white">{card.title}</p>
      {visitor && card.locality_label && <p className="text-xs text-cyan-100">{card.locality_label}</p>}
      {visitor && card.time_zone && <p className="text-xs text-white/60">{card.time_zone}</p>}
      {card.subtitle && <p className="mt-1 break-words text-xs leading-5 text-white/55">{card.subtitle}</p>}
      {href && (
        <a href={href} onClick={() => { void trackVibeBeeJourneyClick(primaryJourneyEvent, card); }} target={card.external_url ? "_blank" : undefined} rel={card.external_url ? "noreferrer" : undefined} className="mt-3 inline-flex min-h-[38px] max-w-full items-center gap-2 rounded-xl bg-gradient-to-r from-yellow-300 via-amber-400 to-fuchsia-500 px-3 py-2 text-xs font-black text-black">
          <span className="min-w-0 break-words">{card.action_label || "Open"}</span>
          <ExternalLink className="shrink-0" size={13} />
        </a>
      )}
      {card.take_me_there_url && !card.external_url && (
        <a href={card.take_me_there_url} onClick={() => { void trackVibeBeeJourneyClick("vibebee_take_me_there", card); }} target="_blank" rel="noreferrer" className="ml-2 mt-3 inline-flex min-h-[38px] max-w-full items-center gap-2 rounded-xl border border-cyan-200/30 bg-cyan-300/10 px-3 py-2 text-xs font-black text-cyan-100">
          <MapPin className="shrink-0" size={13} /> <span className="min-w-0 break-words">Take Me There</span>
        </a>
      )}
    </div>
  );
}

function VibeBeeAvatar({ mascotSrc, compact = false }) {
  if (!mascotSrc) {
    return (
      <span className={`${compact ? "h-8 w-8" : "h-10 w-10"} inline-flex shrink-0 items-center justify-center rounded-full border border-fuchsia-300/30 bg-fuchsia-400/12 text-fuchsia-100`}>
        <Bot size={compact ? 15 : 18} />
      </span>
    );
  }

  return (
    <img
      src={mascotSrc}
      alt="VibeBee"
      className={`${compact ? "h-8 w-8" : "h-10 w-10"} shrink-0 object-contain drop-shadow-[0_0_18px_rgba(255,46,196,0.22)]`}
      loading="lazy"
      decoding="async"
      width="40"
      height="40"
    />
  );
}

export default function VibeBeeChatPanel({
  surface = "public",
  active = true,
  title = "Ask VibeBee",
  intro = "Tell me what kind of night you want — food, drinks, live music or all three. I’ll build it from Vibe2Nite’s verified places and events.",
  mascotSrc = "",
  polishedPublic = false,
  initialContext = null,
  onQuestion = null,
}) {
  const [restoredSession] = React.useState(() => surface !== "admin" && typeof window !== "undefined" ? loadVisitorSession(visitorStorage()) : null);
  const [visitorControls, setVisitorControls] = React.useState(null);
  const [failedTurn, setFailedTurn] = React.useState(null);
  const [preferencesOpen, setPreferencesOpen] = React.useState(false);
  const [restoring, setRestoring] = React.useState(Boolean(restoredSession));
  const requestLock = React.useRef(false);
  const restoredOnce = React.useRef(false);
  const inputRef = React.useRef(null);
  const [input, setInput] = React.useState("");
  const [turns, setTurns] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [voiceMessage, setVoiceMessage] = React.useState("");
  const [voiceState, setVoiceState] = React.useState(VOICE_UI_STATES.IDLE);
  const [voiceReplayReady, setVoiceReplayReady] = React.useState(false);
  const [manualVoiceReady, setManualVoiceReady] = React.useState(false);
  const [voiceRetryReady, setVoiceRetryReady] = React.useState(false);
  const [speechMuted, setSpeechMuted] = React.useState(() => {
    if (typeof window === "undefined") return true;
    try { return window.localStorage.getItem(SPEECH_PREFERENCE_KEY) !== "enabled"; }
    catch (_) { return true; }
  });
  const [posterAttachments, setPosterAttachments] = React.useState([]);
  const [posterOpen, setPosterOpen] = React.useState(false);
  const [posterDragActive, setPosterDragActive] = React.useState(false);
  const [adminPosterContext, setAdminPosterContext] = React.useState({});
  const [posterProgress, setPosterProgress] = React.useState("");
  const initialLanguage = typeof document !== "undefined" && document.documentElement.lang?.startsWith("es") ? "es" : "en";
  const seededContext = restoredSession || initialContext || { language: initialLanguage };
  const [conversationContext, setConversationContext] = React.useState(() => publicConversationContext(seededContext));
  const [contextEnvelope, setContextEnvelope] = React.useState(() => publicContextEnvelope(
    seededContext,
    surface === "admin" ? "admin" : "public",
  ));
  const activeRef = React.useRef(active);
  activeRef.current = active;
  const recognitionRef = React.useRef(null);
  const quartzPlaybackRef = React.useRef(null);
  const retryPresentationRef = React.useRef(null);
  const revealRef = React.useRef(null);
  const turnGenerationRef = React.useRef(0);
  const timingRef = React.useRef(null);
  const posterInputRef = React.useRef(null);
  const posterAttachmentsRef = React.useRef([]);
  const isAdmin = surface === "admin";
  const voiceReady = supportsVoiceInput();
  const showPublicExperience = polishedPublic && !isAdmin;
  const spanish = !isAdmin && conversationContext.language === "es";
  const speechLanguage = spanish ? "es-ES" : "en-GB";
  const request = isAdmin ? askAdminVibeBeeLiveAgent : askPublicVibeBee;
  const listening = [VOICE_UI_STATES.REQUESTING_PERMISSION, VOICE_UI_STATES.LISTENING].includes(voiceState);
  const speaking = voiceState === VOICE_UI_STATES.SPEAKING;

  if (!quartzPlaybackRef.current) {
    quartzPlaybackRef.current = createQuartzPlaybackController({
      requestAudio: requestVibeBeeSpeech,
      reportEvent: recordVibeBeeVoiceEvent,
    });
  }

  React.useEffect(() => {
    const stopVoiceForNavigation = (event) => {
      if (event?.type === "pagehide") {
        revealRef.current?.finish();
        setBusy(false);
        requestLock.current = false;
        setVoiceState(VOICE_UI_STATES.IDLE);
        setVoiceReplayReady(false);
        setVoiceRetryReady(false);
        setManualVoiceReady(false);
      }
      turnGenerationRef.current += 1;
      revealRef.current?.dispose();
      cancelVoiceInput(recognitionRef.current);
      recognitionRef.current = null;
      stopVibeBeeSpeech();
      quartzPlaybackRef.current?.dispose();
      retryPresentationRef.current = null;
    };
    window.addEventListener("pagehide", stopVoiceForNavigation);
    return () => {
      window.removeEventListener("pagehide", stopVoiceForNavigation);
      stopVoiceForNavigation();
      revokePosterItems(posterAttachmentsRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (active) return;
    cancelVoiceInput(recognitionRef.current);
    recognitionRef.current = null;
    quartzPlaybackRef.current?.stop();
    stopVibeBeeSpeech();
    setVoiceState(VOICE_UI_STATES.IDLE);
  }, [active]);

  React.useLayoutEffect(() => {
    const timing = timingRef.current;
    if (timing && !timing.text && turns.some((turn) => turn.id === timing.id)) {
      timing.text = true;
      recordVibeBeeTiming("first_visible_text", timing.started, surface);
    }
  }, [surface, turns]);

  React.useEffect(() => {
    posterAttachmentsRef.current = posterAttachments;
  }, [posterAttachments]);


  const clearPosterAttachments = React.useCallback(() => {
    setPosterAttachments((current) => {
      revokePosterItems(current);
      return [];
    });
    if (posterInputRef.current) posterInputRef.current.value = "";
  }, []);

  const attachPosterFiles = React.useCallback((files) => {
    setPosterOpen(true);
    setPosterAttachments((current) => {
      const result = addPosterFiles(current, files);
      setError(result.error);
      return result.items;
    });
  }, []);

  const openPosterPicker = React.useCallback(() => {
    if (busy || posterAttachments.length >= MAX_VIBEBEE_POSTERS) return;
    posterInputRef.current?.click();
  }, [busy, posterAttachments.length]);

  const removePosterAttachment = React.useCallback((id) => {
    setPosterAttachments((current) => {
      const removed = current.filter((item) => item.id === id);
      revokePosterItems(removed);
      return current.filter((item) => item.id !== id);
    });
  }, []);

  const ask = React.useCallback(async (message, inputMode = "text", visitorAction = null, retry = false) => {
    const hasPosters = isAdmin && posterAttachments.length > 0;
    const clean = String(message || "").trim() || (hasPosters ? "Process these posters." : "");
    if (!clean || busy || (!isAdmin && requestLock.current)) return;
    if (!isAdmin) requestLock.current = true;
    revealRef.current?.finish();
    const generation = ++turnGenerationRef.current;
    const started = performance.now();
    const history = retry && failedTurn ? failedTurn.history : turns.map((turn) => ({ ...turn, content: turn.fullContent || turn.content }));
    stopVibeBeeSpeech();
    quartzPlaybackRef.current?.stop({ release: true });
    setVoiceReplayReady(false);
    setManualVoiceReady(false);
    setVoiceRetryReady(false);
    retryPresentationRef.current = null;
    setVoiceState(inputMode === "voice" ? VOICE_UI_STATES.PROCESSING : VOICE_UI_STATES.IDLE);
    setBusy(true);
    setError("");
    onQuestion?.({ message: clean, inputMode });
    setInput("");
    const userContent = hasPosters ? `${clean}\n\n${posterAttachments.length} poster image${posterAttachments.length === 1 ? "" : "s"} attached.` : clean;
    const nextTurns = visitorAction?.operation === "restore" ? history : [...history, { role: "user", content: userContent }].slice(-8);
    setTurns(nextTurns);
    try {
      let response;
      if (hasPosters) {
        setPosterProgress(`${posterAttachments.length} image${posterAttachments.length === 1 ? "" : "s"} received. Submitting for review-only processing...`);
        const accepted = await submitEventIntakeBatch({
          files: posterAttachments.map((item) => item.file),
          caption: clean,
          sourceUrl: explicitFacebookSourceUrl(clean),
          processMode: "review_only",
        });
        clearPosterAttachments();
        const nextPosterContext = { batch_id: accepted.batch_id, pending_publish: {} };
        setAdminPosterContext(nextPosterContext);
        await pollEventIntakeBatch(getEventIntakeBatch, accepted.batch_id, {
          onProgress: (batch) => setPosterProgress(
            batch.status === "processing"
              ? `Processing ${batch.input_count} image${batch.input_count === 1 ? "" : "s"}: ${batch.event_blocks_detected || 0} event item${batch.event_blocks_detected === 1 ? "" : "s"} detected so far.`
              : `${batch.input_count} image${batch.input_count === 1 ? "" : "s"} received. ${batch.status.replaceAll("_", " ")}.`,
          ),
        });
        response = await askAdminVibeBeeLiveAgent(clean, history, null, nextPosterContext, contextEnvelope, inputMode);
      } else {
        response = isAdmin
          ? await request(clean, history, null, adminPosterContext, contextEnvelope, inputMode)
          : await request(clean, history, null, contextEnvelope, inputMode, visitorAction);
      }
      if (generation !== turnGenerationRef.current) return;
      if (!response || typeof response.answer !== "string" || !response.answer.trim()) throw new Error("empty_response");
      setFailedTurn(null); setRestoring(false);
      if (!isAdmin) setVisitorControls(response.conversation_context?.visitor || null);
      recordVibeBeeTiming("validated_response", started, surface);
      const id = `turn-${generation}`;
      timingRef.current = { id, started, text: false };
      setTurns((current) => [
        ...current,
        { id, role: "assistant", content: response.answer, fullContent: response.answer, cards: response.cards || [], research: isAdmin ? response.research : null, speechPresentation: response.speech_presentation || null, provider: isAdmin ? response.provider : null, safety: isAdmin ? response.safety : null },
      ].slice(-10));
      if (isAdmin && response.admin_poster_context) setAdminPosterContext(response.admin_poster_context);
      const receivedContext = response.context_envelope || response.conversation_context?.context_envelope || response.conversation_context;
      const nextEnvelope = isAdmin
        ? publicContextEnvelope(receivedContext || contextEnvelope, "admin")
        : mergeVisitorContext(contextEnvelope, receivedContext);
      setContextEnvelope(nextEnvelope);
      if (!isAdmin) {
        const nextConversation = publicConversationContext(nextEnvelope);
        setConversationContext(nextConversation);
        rememberVibeBeeJourney(nextConversation.journey_id);
        saveVisitorSession(visitorStorage(), nextEnvelope, response.conversation_context?.visitor?.resolved_date);
      }
      setPosterProgress("");
      const presentation = response.speech_presentation;
      const language = presentation?.language || response.conversation_context?.language || nextEnvelope.conversation?.language;
      const shouldSpeak = Boolean(activeRef.current && presentation && (presentation.autoplay || !speechMuted));
      // Chat is complete. A new turn can interrupt even while TTS is pending.
      setBusy(false);
      requestLock.current = false;
      if (shouldSpeak && presentation?.synthesis_token && language === "en" && (isAdmin || presentation.surface !== "admin")) {
        revealRef.current = createValidatedTextReveal(response.answer, (content) => {
          if (generation !== turnGenerationRef.current) return;
          setTurns((current) => current.map((turn) => turn.id === id ? { ...turn, content } : turn));
        });
        const guarded = (callback) => (...args) => {
          if (generation === turnGenerationRef.current) callback(...args);
        };
        const quartz = await quartzPlaybackRef.current.synthesizeAndPlay(presentation, {
          onSynthesizing: guarded(() => setVoiceState(VOICE_UI_STATES.SYNTHESIZING)),
          onStart: guarded(() => {
            recordVibeBeeTiming("first_audio", started, surface);
            revealRef.current?.start();
            setVoiceMessage("");
            setManualVoiceReady(false);
            setVoiceRetryReady(false);
            retryPresentationRef.current = null;
            setVoiceState(VOICE_UI_STATES.SPEAKING);
          }),
          onEnd: guarded(() => {
            revealRef.current?.finish();
            recordVibeBeeTiming("response_complete", started, surface);
            setVoiceReplayReady(true);
            setVoiceState(VOICE_UI_STATES.IDLE);
          }),
          onAutoplayBlocked: guarded(() => {
            revealRef.current?.finish();
            setVoiceRetryReady(false);
            retryPresentationRef.current = null;
            setManualVoiceReady(true);
            setVoiceReplayReady(true);
            setVoiceState(VOICE_UI_STATES.READY_TO_SPEAK);
          }),
          onError: guarded(() => {
            revealRef.current?.finish();
            retryPresentationRef.current = presentation;
            setVoiceRetryReady(true);
            setManualVoiceReady(false);
            setVoiceMessage("VibeBee's voice isn't available right now. The written answer is still here.");
            setVoiceState(VOICE_UI_STATES.ERROR);
          }),
        });
        if (generation !== turnGenerationRef.current) return;
        if (["failed", "unavailable"].includes(quartz.status)) {
          revealRef.current?.finish();
          retryPresentationRef.current = presentation;
          setVoiceRetryReady(true);
          setManualVoiceReady(false);
          setVoiceMessage("VibeBee's voice isn't available right now. The written answer is still here.");
          setVoiceState(VOICE_UI_STATES.ERROR);
        }
      } else if (inputMode === "voice" && shouldSpeak) {
        setVoiceMessage("VibeBee's voice isn't available right now. The written answer is still here.");
        setVoiceState(VOICE_UI_STATES.ERROR);
      }
    } catch (err) {
      if (generation !== turnGenerationRef.current) return;
      revealRef.current?.finish();
      if (!isAdmin) {
        recordVibeBeeTiming("request_failed", started, surface);
        setFailedTurn({ message: clean, inputMode, visitorAction, history });
      }
      setRestoring(false);
      setError(isAdmin ? err?.response?.data?.detail || err?.message || "VibeBee could not answer just now." : err?.response?.data?.detail || (spanish ? "VibeBee no puede responder ahora. Inténtalo de nuevo." : "VibeBee could not answer just now. Your choices are kept. Please try again."));
      if (inputMode === "voice") setVoiceState(VOICE_UI_STATES.ERROR);
    } finally {
      if (generation === turnGenerationRef.current) { setBusy(false); requestLock.current = false; }
    }
  }, [adminPosterContext, busy, failedTurn, clearPosterAttachments, contextEnvelope, isAdmin, onQuestion, posterAttachments, request, spanish, speechMuted, surface, turns]);

  React.useEffect(() => {
    if (isAdmin || !restoredSession || restoredOnce.current || !active) return;
    restoredOnce.current = true;
    void ask("Show my saved plan", "text", { operation: "restore" });
  }, [active, ask, isAdmin, restoredSession]);

  function startAgain() {
    turnGenerationRef.current += 1;
    requestLock.current = false;
    revealRef.current?.dispose();
    cancelVoiceInput(recognitionRef.current);
    quartzPlaybackRef.current?.stop();
    clearVisitorSession(visitorStorage());
    setTurns([]); setInput(""); setError(""); setFailedTurn(null);
    setBusy(false); setRestoring(false); setVisitorControls(null);
    setVoiceState(VOICE_UI_STATES.IDLE);
    const fresh = { language: conversationContext.language };
    setConversationContext(publicConversationContext(fresh));
    setContextEnvelope(publicContextEnvelope(fresh));
    inputRef.current?.focus();
  }

  function onSubmit(event) {
    event.preventDefault();
    ask(input, "text");
  }

  function onPosterPaste(event) {
    if (!isAdmin) return;
    const images = clipboardImageFiles(event.clipboardData);
    if (!images.length) return;
    event.preventDefault();
    event.stopPropagation();
    setPosterOpen(true);
    attachPosterFiles(images);
  }

  function onPosterDrop(event) {
    if (!isAdmin) return;
    event.preventDefault();
    setPosterDragActive(false);
    attachPosterFiles(event.dataTransfer?.files);
  }

  function onPosterDragOver(event) {
    if (!isAdmin) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setPosterDragActive(true);
  }

  function onPosterDragLeave(event) {
    if (!isAdmin || event.currentTarget.contains(event.relatedTarget)) return;
    setPosterDragActive(false);
  }

  function onPosterZoneClick(event) {
    event.currentTarget.focus();
  }

  function onVoice() {
    revealRef.current?.finish();
    setVoiceMessage("");
    stopVibeBeeSpeech();
    quartzPlaybackRef.current?.stop();
    if (listening) {
      cancelVoiceInput(recognitionRef.current);
      recognitionRef.current = null;
      setVoiceState(VOICE_UI_STATES.IDLE);
      return;
    }
    setVoiceState(VOICE_UI_STATES.REQUESTING_PERMISSION);
    let submitted = false;
    let failed = false;
    recognitionRef.current = startVoiceInput({
      lang: speechLanguage,
      onStart: () => setVoiceState(VOICE_UI_STATES.LISTENING),
      onEnd: () => {
        recognitionRef.current = null;
        if (!submitted && !failed) setVoiceState(VOICE_UI_STATES.IDLE);
      },
      onResult: (transcript) => {
        submitted = true;
        setInput(transcript);
        setVoiceState(VOICE_UI_STATES.PROCESSING);
        ask(transcript, "voice");
      },
      onError: (message) => {
        failed = true;
        setVoiceMessage(message);
        setVoiceState(VOICE_UI_STATES.ERROR);
      },
      onNoMatch: (message) => {
        failed = true;
        setVoiceMessage(message);
        setVoiceState(VOICE_UI_STATES.ERROR);
      },
      onTimeout: (message) => {
        failed = true;
        setVoiceMessage(message);
        setVoiceState(VOICE_UI_STATES.ERROR);
      },
    });
    if (!recognitionRef.current) setVoiceState(VOICE_UI_STATES.ERROR);
  }

  async function onRetryVoice() {
    const presentation = retryPresentationRef.current;
    if (!presentation) return;
    const result = await quartzPlaybackRef.current?.synthesizeAndPlay(presentation, {
      onSynthesizing: () => setVoiceState(VOICE_UI_STATES.SYNTHESIZING),
      onStart: () => {
        setVoiceMessage("");
        setVoiceRetryReady(false);
        retryPresentationRef.current = null;
        setVoiceState(VOICE_UI_STATES.SPEAKING);
      },
      onEnd: () => {
        setVoiceReplayReady(true);
        setVoiceState(VOICE_UI_STATES.IDLE);
      },
      onAutoplayBlocked: () => {
        setVoiceRetryReady(false);
        retryPresentationRef.current = null;
        setManualVoiceReady(true);
        setVoiceReplayReady(true);
        setVoiceState(VOICE_UI_STATES.READY_TO_SPEAK);
      },
      onError: (message) => {
        retryPresentationRef.current = presentation;
        setVoiceRetryReady(true);
        setVoiceMessage(message);
        setVoiceState(VOICE_UI_STATES.ERROR);
      },
    });
    if (!result || ["failed", "unavailable"].includes(result.status)) {
      retryPresentationRef.current = presentation;
      setVoiceRetryReady(true);
      setVoiceMessage("VibeBee's voice isn't available right now. The written answer is still here.");
      setVoiceState(VOICE_UI_STATES.ERROR);
    }
  }

  function onStopSpeaking() {
    revealRef.current?.finish();
    stopVibeBeeSpeech();
    quartzPlaybackRef.current?.stop();
    setVoiceReplayReady(quartzPlaybackRef.current?.hasReplay() || false);
    setVoiceState(VOICE_UI_STATES.IDLE);
  }

  async function onPlayVoice(replay = false) {
    const callbacks = {
      onStart: () => {
        setManualVoiceReady(false);
        setVoiceState(VOICE_UI_STATES.SPEAKING);
      },
      onEnd: () => {
        setVoiceReplayReady(true);
        setVoiceState(VOICE_UI_STATES.IDLE);
      },
      onError: (message) => {
        setVoiceMessage(message);
        setVoiceState(VOICE_UI_STATES.ERROR);
      },
      onAutoplayBlocked: () => {
        setManualVoiceReady(true);
        setVoiceState(VOICE_UI_STATES.READY_TO_SPEAK);
      },
    };
    const result = replay
      ? await quartzPlaybackRef.current?.replay(callbacks)
      : await quartzPlaybackRef.current?.play(callbacks);
    if (!result || result.status === "unavailable") {
      setVoiceMessage("VibeBee's voice isn't available right now. The written answer is still here.");
      setVoiceState(VOICE_UI_STATES.ERROR);
    }
  }

  function toggleSpokenAnswers() {
    setSpeechMuted((value) => {
      const next = !value;
      if (typeof window !== "undefined") {
        try { window.localStorage.setItem(SPEECH_PREFERENCE_KEY, next ? "muted" : "enabled"); }
        catch (_) { /* The current tab's explicit preference still applies. */ }
      }
      if (next) {
        revealRef.current?.finish();
        stopVibeBeeSpeech();
        quartzPlaybackRef.current?.stop();
        setVoiceReplayReady(quartzPlaybackRef.current?.hasReplay() || false);
        setVoiceState(VOICE_UI_STATES.IDLE);
      }
      return next;
    });
  }

  return (
    <section className={`${showPublicExperience ? "rounded-[30px] border-fuchsia-300/18 bg-[#0d1020]/82 shadow-[0_28px_90px_rgba(0,0,0,0.36)]" : "rounded-3xl border-cyan-200/15 bg-black/35 shadow-[0_0_40px_rgba(34,211,238,0.08)]"} flex ${isAdmin ? "" : "min-h-[620px]"} w-full max-w-full min-w-0 box-border flex-col border p-4 backdrop-blur-2xl sm:p-5`}>
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 gap-3">
          {showPublicExperience && <VibeBeeAvatar mascotSrc={mascotSrc} />}
          <div className="min-w-0">
            <p className="flex min-w-0 items-center gap-2 break-words text-xs font-black uppercase tracking-[0.2em] text-cyan-200"><Bot className="shrink-0" size={15} /> {isAdmin ? "Admin VibeBee" : "VibeBee Concierge"}</p>
            <h2 className={`${showPublicExperience ? "text-2xl sm:text-3xl" : "text-3xl sm:text-4xl"} mt-2 break-words font-black text-white`}>{title}</h2>
            <p className="mt-2 max-w-2xl break-words text-sm leading-6 text-white/60">{intro}</p>
          </div>
        </div>
        <div className="flex min-w-0 flex-wrap gap-2">
          <span className="inline-flex max-w-full items-center gap-2 rounded-full border border-emerald-200/25 bg-emerald-300/10 px-3 py-2 text-xs font-black text-emerald-100"><ShieldCheck className="shrink-0" size={14} /> {isAdmin ? "Research prepares drafts · publishing needs approval" : "Read only"}</span>
          <span className="inline-flex max-w-full items-center gap-2 rounded-full border border-fuchsia-200/25 bg-fuchsia-300/10 px-3 py-2 text-xs font-black text-fuchsia-100"><Mic className="shrink-0" size={14} /> {voiceReady ? "Voice input available" : "Text only"}</span>
        </div>
      </div>

      {!isAdmin && <section aria-label={spanish ? "Tus preferencias" : "Your preferences"} className="mt-3 rounded-2xl border border-white/10 p-3">
        <p className="text-sm text-white/80">{visitorSummary(conversationContext, spanish).join(" · ") || (spanish ? "Elige tu zona y lo que te apetece." : "Tell me your area and what you fancy.")}</p>
        <div className="flex flex-wrap gap-3">
          <button type="button" className="min-h-[44px] text-sm text-cyan-200" aria-expanded={preferencesOpen} onClick={() => { setPreferencesOpen(v => !v); inputRef.current?.focus(); }}>{spanish ? "Cambiar preferencias" : "Change preferences"}</button>
          <button type="button" className="min-h-[44px] text-sm text-white/70" onClick={startAgain}>{spanish ? "Empezar de nuevo" : "Start again"}</button>
        </div>
        {preferencesOpen && <p className="text-sm text-white/65">{spanish ? "Dime qué cambiar: zona, fecha, comida, presupuesto o transporte. Mantendré lo demás." : "Tell me what to change: area, date, food, budget or transport. I’ll keep the other choices."}</p>}
      </section>}
      {isAdmin && <button type="button" disabled={busy} onClick={() => ask("Research all the entertainment happening tonight in our three Spanish areas and prepare anything missing", "text")} className="mt-4 rounded border border-cyan-200/30 p-3 text-cyan-100">Research tonight’s entertainment</button>}
      {showPublicExperience && turns.length === 0 && (
        <div className="mt-5 flex min-w-0 flex-wrap gap-2 pb-1">
          {QUICK_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => ask(prompt, "text")}
              disabled={busy}
              className="min-h-[42px] max-w-full min-w-0 rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-left text-sm font-bold text-white/82 transition hover:border-[#f0c66a]/35 hover:bg-white/[0.09] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="block min-w-0 break-words">{prompt}</span>
            </button>
          ))}
        </div>
      )}

      <div className={`${isAdmin ? "order-2 max-h-[50vh]" : "min-h-[250px]"} mt-4 min-w-0 flex-1 space-y-3 overflow-y-auto pr-1`}>
        {turns.length === 0 && !isAdmin && (
          <div className={`${showPublicExperience ? "border-fuchsia-300/16 bg-white/[0.055]" : "border-white/10 bg-white/[0.04]"} min-w-0 rounded-2xl border p-4 text-sm leading-6 text-white/60`}>
            Tell me the flow you want, like “dinner, drinks, then music”, and I’ll keep the places and events grounded in Vibe2Nite.
          </div>
        )}
        {turns.length === 0 && isAdmin && (
          <div className="min-w-0 rounded-2xl border border-cyan-200/15 bg-cyan-300/[0.05] p-4 text-sm leading-6 text-white/65">
            Ask what needs attention, check What's On, inspect venue or event issues, or add supplied event posters for review.
          </div>
        )}
        {turns.map((turn, index) => {
          const isUser = turn.role === "user";
          return (
            <div key={`${turn.role}-${index}`} className={`flex min-w-0 gap-2 ${isUser ? "justify-end" : "justify-start"}`}>
              {!isUser && showPublicExperience && <VibeBeeAvatar mascotSrc={mascotSrc} compact />}
              <div className={`max-w-[88%] min-w-0 break-words rounded-2xl border p-3 ${isUser ? "border-[#f0c66a]/30 bg-[#f0c66a]/12 text-right" : "border-fuchsia-200/18 bg-white/[0.06]"}`}>
                <p className="text-[10px] font-black uppercase tracking-[0.16em] text-white/35">{isUser ? "You" : "VibeBee"}</p>
                <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-white/78">{turn.content}</p>
                {!isUser && turn.fullContent !== turn.content && <button type="button" className="min-h-[44px] text-xs font-bold text-cyan-200" onClick={() => revealRef.current?.finish()}>Show complete response</button>}
                {isAdmin && turn.research && <VibeBeeResearchResult research={turn.research} />}
                {!!turn.cards?.length && <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-2">{turn.cards.map((card, cardIndex) => <Card key={`${card.title}-${cardIndex}`} card={card} visitor={!isAdmin} />)}</div>}
                {isAdmin && turn.provider && <p className="mt-2 break-words text-[11px] text-white/35">Mode: {turn.provider.mode}. Paid model call made: {turn.safety?.paid_model_call_made ? "Yes" : "No"}.</p>}
              </div>
            </div>
          );
        })}
      </div>

      {!isAdmin && visitorControls?.choices?.length > 0 && <div className="mt-3 flex flex-wrap gap-2" aria-label="Choose your starting area">{visitorControls.choices.map(choice => <button type="button" className="min-h-[44px] rounded-xl border border-cyan-200/30 px-3 text-sm text-cyan-100" disabled={busy} key={choice} onClick={() => ask(choice)}>{choice}</button>)}</div>}
      {!isAdmin && !restoring && visitorControls?.actions?.length > 0 && <div className="mt-3 flex flex-wrap gap-2" aria-label="Refine your current plan">{visitorControls.actions.slice(0,3).map(action => <button type="button" key={action.operation} disabled={busy} className="min-h-[44px] rounded-xl border border-cyan-200/30 px-3 text-sm text-cyan-100" onClick={() => ask(action.label, "text", action)}>{action.label}</button>)}</div>}
      {!isAdmin && !busy && conversationContext.night_plan.stops.length > 0 && !visitorControls && <p role="status" className="mt-3 text-sm text-amber-100">{spanish ? "Los controles del plan no están disponibles. Puedes escribir tu cambio." : "Plan controls aren’t available from this response. You can still type your change."}</p>}
      {!isAdmin && failedTurn && <button type="button" disabled={busy} className="mt-2 min-h-[44px] rounded-xl border border-amber-200/30 px-4 text-amber-100" onClick={() => ask(failedTurn.message, failedTurn.inputMode, failedTurn.visitorAction, true)}>{spanish ? "Reintentar" : "Retry question"}</button>}
      {!isAdmin && failedTurn?.visitorAction && <button type="button" disabled={busy} className="min-h-[44px] px-3 text-cyan-100" onClick={() => ask("Show my saved plan", "text", {operation:"restore"})}>{spanish ? "Actualizar plan" : "Reload saved plan"}</button>}
      {busy && <p role="status" aria-live="polite" className="mt-3 rounded-xl border border-fuchsia-300/20 bg-fuchsia-300/10 p-3 text-xs font-bold text-fuchsia-100">{spanish ? "VibeBee está respondiendo..." : voiceState === VOICE_UI_STATES.PROCESSING ? "VibeBee is thinking..." : isAdmin ? "VibeBee is replying..." : "Checking approved listings…"}</p>}
      {isAdmin && posterProgress && <p className="mt-3 rounded-xl border border-cyan-300/20 bg-cyan-300/10 p-3 text-xs font-bold text-cyan-100">{posterProgress}</p>}
      {voiceState === VOICE_UI_STATES.REQUESTING_PERMISSION && <p className="mt-3 rounded-xl border border-cyan-300/20 bg-cyan-300/10 p-3 text-xs font-bold text-cyan-100">Requesting microphone...</p>}
      {voiceState === VOICE_UI_STATES.LISTENING && <p className="mt-3 rounded-xl border border-cyan-300/20 bg-cyan-300/10 p-3 text-xs font-bold text-cyan-100">Listening...</p>}
      {voiceState === VOICE_UI_STATES.SYNTHESIZING && <p className="mt-3 rounded-xl border border-fuchsia-300/20 bg-fuchsia-300/10 p-3 text-xs font-bold text-fuchsia-100">Preparing VibeBee's voice...</p>}
      {speaking && <p className="mt-3 rounded-xl border border-emerald-300/20 bg-emerald-300/10 p-3 text-xs font-bold text-emerald-100">VibeBee is speaking...</p>}
      {error && <p role="alert" className="mt-3 rounded-xl border border-red-300/25 bg-red-300/10 p-3 text-xs text-red-100">{error}</p>}
      {voiceMessage && <p className="mt-3 rounded-xl border border-cyan-300/20 bg-cyan-300/10 p-3 text-xs text-cyan-100">{voiceMessage}</p>}

      <form
        onSubmit={onSubmit}
        onPaste={isAdmin ? onPosterPaste : undefined}
        className={`${isAdmin ? "order-1" : ""} mt-4 flex min-w-0 flex-wrap gap-2 rounded-[24px] border border-white/10 bg-black/32 p-2`}
      >
        {isAdmin && <>
          <input ref={posterInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="sr-only" onChange={(event) => attachPosterFiles(event.target.files)} />
          {posterOpen && <div
            tabIndex={0}
            aria-label="Event poster drop zone — paste, drop or select up to 20 images"
            data-testid="event-poster-drop-zone"
            onClick={onPosterZoneClick}
            onPaste={onPosterPaste}
            onDrop={onPosterDrop}
            onDragEnter={onPosterDragOver}
            onDragOver={onPosterDragOver}
            onDragLeave={onPosterDragLeave}
            className={`${posterDragActive ? "border-cyan-200 bg-cyan-300/15 shadow-[0_0_34px_rgba(34,211,238,0.18)]" : "border-cyan-200/25 bg-cyan-300/[0.055] hover:border-cyan-200/45 hover:bg-cyan-300/[0.08]"} basis-full cursor-text rounded-2xl border-2 border-dashed p-4 transition outline-none focus:border-cyan-200 focus:ring-2 focus:ring-cyan-200/35 sm:p-5`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-100">Event posters</p>
                <p className="mt-1 text-xs font-bold text-white/45">{posterAttachments.length} / {MAX_VIBEBEE_POSTERS} posters</p>
              </div>
              {posterAttachments.length > 0 && (
                <button type="button" onClick={(event) => { event.stopPropagation(); clearPosterAttachments(); }} className="min-h-[36px] rounded-xl border border-white/10 px-3 text-xs font-black text-white/65 hover:border-white/25 hover:text-white">Clear All</button>
              )}
            </div>

            {posterAttachments.length === 0 ? (
              <div className="flex min-h-[210px] flex-col items-center justify-center px-3 py-6 text-center">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-200/25 bg-cyan-300/10 text-cyan-100"><ImagePlus size={28} /></span>
                <p className="mt-4 text-lg font-black uppercase text-white">{posterDragActive ? "Drop posters to add them" : "Drop Event Posters Here"}</p>
                <p className="mt-2 text-sm leading-6 text-white/60">Paste Ctrl+V / Cmd+V, drag images into this box, or choose images from your device.</p>
                <p className="mt-1 text-xs text-white/40">JPEG, PNG or WebP · maximum {MAX_VIBEBEE_POSTERS}</p>
                <button type="button" onClick={(event) => { event.stopPropagation(); openPosterPicker(); }} disabled={busy} className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-cyan-200 px-4 py-2 text-sm font-black text-[#07111d] disabled:opacity-40"><ImagePlus size={17} /> Choose Images</button>
              </div>
            ) : (
              <div className="mt-4">
                <p className="text-sm font-black text-white">{posterAttachments.length} event poster{posterAttachments.length === 1 ? "" : "s"} attached</p>
                <div className="mt-3 grid max-h-56 grid-cols-3 gap-2 overflow-y-auto pb-1 sm:grid-cols-5 md:grid-cols-6">
                  {posterAttachments.map((item) => (
                    <div key={item.id} className="relative aspect-square min-w-0 overflow-hidden rounded-lg border border-white/10 bg-black/30">
                      <img src={item.previewUrl} alt={item.file.name || "Event poster preview"} className="h-full w-full object-cover" />
                      <button type="button" onClick={(event) => { event.stopPropagation(); removePosterAttachment(item.id); }} aria-label={`Remove ${item.file.name || "poster"}`} className="absolute right-1 top-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/85 text-white"><X size={15} /></button>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button type="button" onClick={(event) => { event.stopPropagation(); openPosterPicker(); }} disabled={busy || posterAttachments.length >= MAX_VIBEBEE_POSTERS} className="inline-flex min-h-[42px] items-center gap-2 rounded-xl border border-cyan-200/25 bg-cyan-300/10 px-4 text-sm font-black text-cyan-100 disabled:cursor-not-allowed disabled:opacity-40"><ImagePlus size={17} /> Add Event Posters</button>
                  {posterAttachments.length >= MAX_VIBEBEE_POSTERS && <p className="text-xs font-bold text-amber-100">Maximum reached. Remove a poster before adding another.</p>}
                </div>
              </div>
            )}
            <button type="button" onClick={event => {event.stopPropagation(); setPosterOpen(false);}} className="min-h-[44px] underline">Close uploader</button>
          </div>}

          {posterAttachments.length > 0 && (
            <div className="basis-full rounded-2xl border border-fuchsia-200/15 bg-fuchsia-300/[0.05] p-3">
              <p className="text-sm font-black text-white">Now tell VibeBee what to do.</p>
              <button type="button" onClick={() => setInput("Add these to What's On")} className="mt-2 min-h-[40px] rounded-xl border border-fuchsia-200/25 bg-fuchsia-300/10 px-3 py-2 text-sm font-black text-fuchsia-100">Add these to What's On</button>
            </div>
          )}
        </>}
        <input ref={inputRef} value={input} maxLength={800} aria-label={spanish ? "Pregunta a VibeBee" : "Ask VibeBee"} onChange={(event) => setInput(event.target.value)} placeholder={spanish ? "Pregunta a VibeBee..." : "Ask VibeBee..."} className="min-h-[52px] min-w-0 basis-full flex-1 rounded-2xl border border-transparent bg-black/35 px-4 py-3 text-base text-white outline-none placeholder:text-white/35 focus:border-primary-glow/40 sm:basis-auto" />
        {isAdmin && <>
          <button type="button" onClick={() => setPosterOpen(open => !open)} aria-expanded={posterOpen} disabled={busy} className="inline-flex min-h-[52px] basis-full items-center justify-center gap-2 rounded-2xl border border-cyan-200/25 bg-cyan-300/10 px-4 text-sm font-black text-cyan-100 disabled:opacity-40 sm:basis-auto" aria-label="Add event posters — paste, drop or select up to 20 images" title="Add event posters — paste, drop or select up to 20 images"><ImagePlus size={18} /><span>Add Event Posters{posterAttachments.length > 0 ? ` (${posterAttachments.length} attached)` : ""}</span></button>
        </>}
        <button type="button" onClick={onVoice} disabled={!voiceReady || busy} className="inline-flex min-h-[52px] shrink-0 items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] px-3 text-white/70 disabled:opacity-40" aria-label={listening ? "Stop listening" : "Talk to VibeBee"}>{listening ? <MicOff size={18} /> : <Mic size={18} />}<span className="hidden text-xs font-black sm:inline">{listening ? "Stop" : "Talk"}</span></button>
        <button type="button" onClick={toggleSpokenAnswers} className="inline-flex min-h-[52px] w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] text-white/70 disabled:opacity-40" aria-label={speechMuted ? "Enable spoken answers" : "Mute spoken answers"}>{speechMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>
        {(speaking || voiceState === VOICE_UI_STATES.SYNTHESIZING) && <button type="button" onClick={onStopSpeaking} className="inline-flex min-h-[52px] shrink-0 items-center rounded-2xl border border-emerald-200/25 bg-emerald-300/10 px-3 py-2 text-xs font-black text-emerald-100">Stop speaking</button>}
        {!speaking && voiceRetryReady && <button type="button" onClick={onRetryVoice} className="inline-flex min-h-[52px] shrink-0 items-center rounded-2xl border border-fuchsia-200/25 bg-fuchsia-300/10 px-3 py-2 text-xs font-black text-fuchsia-100">Retry voice</button>}
        {!speaking && manualVoiceReady && !voiceRetryReady && <button type="button" onClick={() => onPlayVoice(false)} className="inline-flex min-h-[52px] shrink-0 items-center rounded-2xl border border-fuchsia-200/25 bg-fuchsia-300/10 px-3 py-2 text-xs font-black text-fuchsia-100">Play VibeBee</button>}
        {!speaking && voiceReplayReady && !manualVoiceReady && <button type="button" onClick={() => onPlayVoice(true)} className="inline-flex min-h-[52px] shrink-0 items-center rounded-2xl border border-white/15 bg-white/[0.06] px-3 py-2 text-xs font-black text-white/75">Replay</button>}
        <button type="submit" aria-label={spanish ? "Enviar" : "Send"} disabled={busy || (!input.trim() && !posterAttachments.length)} className="inline-flex min-h-[52px] shrink-0 items-center gap-2 rounded-2xl bg-gradient-to-r from-yellow-300 via-amber-400 to-fuchsia-500 px-4 py-3 text-sm font-black text-black disabled:opacity-50"><Send size={16} /><span className="hidden sm:inline">{spanish ? "Enviar" : "Send"}</span></button>
      </form>
          {isAdmin && <p className="order-3 mt-3 flex min-w-0 items-center gap-2 break-words text-[11px] leading-5 text-white/35"><Sparkles className="shrink-0" size={13} /> It cannot award points, change QR codes, send alerts, book tables, mutate production data or edit admin data from this chat. The only exception is explicitly confirmed, exact-batch event publishing through the existing safe-publish gate.</p>}
    </section>
  );
}
